import { z, type ZodType } from 'zod'
import type { Tier } from '@/lib/tiers'

/**
 * Runtime zod schemas mirroring the highest-risk API payloads in
 * `src/types/api.ts`. Added by CS-006 WITHOUT wiring into call sites yet —
 * adoption happens in CS-015.
 *
 * Conventions:
 * - Objects use `.passthrough()` so unknown extra backend fields never throw.
 * - Nullability/optionality mirrors the TS interfaces exactly to avoid false
 *   rejections (`.nullable()` for `T | null`, `.optional()` for `field?`).
 * - `src/types/api.ts` remains the source of truth; the `z.infer` aliases here
 *   are kept structurally aligned (typecheck enforces drift).
 */

// ─── Shared leaf schemas ──────────────────────────────────────────────────────

export const PreviewColorsSchema = z
  .object({
    terrain: z.string().nullable().optional(),
    trail: z.string().nullable().optional(),
    water: z.string().nullable().optional(),
  })
  .passthrough()

export const ElevationSplitsWireSchema = z
  .object({
    z_mm: z.number(),
    color_hex: z.string(),
  })
  .passthrough()

export const PaintModeSchema = z.enum(['elevation', 'satellite', 'solid'])

// ─── Paint layers (discriminated union over params.kind) ──────────────────────

export const SatellitePaletteParamsSchema = z
  .object({
    kind: z.literal('satellite_palette'),
    // applied_paint is a passthrough blob: numeric layer params originate from
    // frontend form/<select> values and have historically been persisted as
    // DynamoDB String attributes (e.g. seeded campaigns), so they can arrive as
    // stringified numbers. Coerce so a "1" never fails validation. See
    // band_index / satellite_k below for the same reasoning.
    palette_index: z.coerce.number(),
  })
  .passthrough()

export const TrailParamsSchema = z
  .object({
    kind: z.literal('trail'),
  })
  .passthrough()

export const LakeParamsSchema = z
  .object({
    kind: z.literal('lake'),
  })
  .passthrough()

export const ElevationBandParamsSchema = z
  .object({
    kind: z.literal('elevation_band'),
    // Coerced: persisted applied_paint can hold band_index as a string ("0").
    // See SatellitePaletteParamsSchema.palette_index for the rationale.
    band_index: z.coerce.number(),
  })
  .passthrough()

export const MarkerParamsSchema = z
  .object({
    kind: z.literal('marker'),
    // Coerced: persisted applied_paint can hold marker_index as a string ("0").
    // See SatellitePaletteParamsSchema.palette_index for the rationale.
    marker_index: z.coerce.number(),
  })
  .passthrough()

export const PaintLayerParamsSchema = z.discriminatedUnion('kind', [
  SatellitePaletteParamsSchema,
  TrailParamsSchema,
  LakeParamsSchema,
  ElevationBandParamsSchema,
  MarkerParamsSchema,
])

export const PaintLayerSchema = z
  .object({
    enabled: z.boolean(),
    color_hex: z.string(),
    label: z.string().nullable().optional(),
    params: PaintLayerParamsSchema,
  })
  .passthrough()

// ─── Map endpoints ────────────────────────────────────────────────────────────

export const TrailSummarySchema = z
  .object({
    id: z.number(),
    name: z.string().nullable(),
    length_km: z.number(),
    type: z.enum(['route', 'way']),
    lng: z.number(),
    lat: z.number(),
    geojson: z.unknown().nullable().optional(),
  })
  .passthrough()

export const LakeSummarySchema = z
  .object({
    id: z.number(),
    name: z.string().nullable(),
    water_type: z.string().nullable(),
    area_km2: z.number(),
    lng: z.number(),
    lat: z.number(),
    geojson: z.unknown().optional(),
  })
  .passthrough()

export const TrailClusterSchema = z
  .object({
    lng: z.number(),
    lat: z.number(),
    count: z.number(),
    sample_name: z.string().nullable(),
  })
  .passthrough()

export const MapResponseSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('clusters'),
      data: z.array(TrailClusterSchema),
      total: z.number().optional(),
    })
    .passthrough(),
  z
    .object({
      type: z.literal('trails'),
      data: z.array(TrailSummarySchema),
      total: z.number().optional(),
    })
    .passthrough(),
  z
    .object({
      type: z.literal('mixed'),
      features: z.array(TrailSummarySchema),
      clusters: z.array(TrailClusterSchema),
    })
    .passthrough(),
])

export const LakeMapResponseSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('clusters'),
      data: z.array(TrailClusterSchema),
    })
    .passthrough(),
  z
    .object({
      type: z.literal('lakes'),
      data: z.array(LakeSummarySchema),
    })
    .passthrough(),
  z
    .object({
      type: z.literal('mixed'),
      features: z.array(LakeSummarySchema),
      clusters: z.array(TrailClusterSchema),
    })
    .passthrough(),
])

// ─── Job status ───────────────────────────────────────────────────────────────

const PhaseKeySchema = z.enum(['preflight', 'satellite', 'terrain', 'export'])

export const PlannedStepSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    complete: z.boolean(),
  })
  .passthrough()

export const PlannedSubStepSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    complete: z.boolean(),
  })
  .passthrough()

export const PlannedPhaseSchema = z
  .object({
    key: PhaseKeySchema,
    label: z.string(),
    parallel_group: z.string().nullable(),
    sub_steps: z.array(PlannedSubStepSchema),
    all_complete: z.boolean(),
  })
  .passthrough()

export const JobConfigSchema = z
  .object({
    input_type: z.enum(['trail_id', 'lake_id', 'bounds', 'gpx_upload']),
    // Backend serializes these as `null` (Pydantic `list[int] | None`) for the
    // input types that don't use them, so they must be nullish, not just optional.
    trail_ids: z.array(z.number()).nullable().optional(),
    lake_ids: z.array(z.number()).nullable().optional(),
    // Present for the gpx_upload path; absent for the other input types.
    upload_id: z.string().optional(),
    bounds: z
      .object({
        north: z.number(),
        south: z.number(),
        east: z.number(),
        west: z.number(),
      })
      .passthrough(),
    size_mm: z.number(),
    resolution: z.enum(['standard', 'high']),
    // Vertical exaggeration; null/omitted = backend auto-computes. Mirrors the
    // JobConfig interface in types/api.ts so a status echo round-trips typed.
    z_scale: z.number().nullable().optional(),
    puzzle: z.boolean(),
    // R1: server-derived reduced W:H for the explicit size form ("3:2"), so it
    // can no longer be the '1:1' literal — this schema also parses GET /status,
    // and a strict literal would blank the job page for any rectangular job.
    aspect_ratio: z.string().nullable().optional(),
    // R1 explicit size form; null/absent on every legacy config.
    width_mm: z.number().nullable().optional(),
    height_mm: z.number().nullable().optional(),
    cols: z.number().nullable().optional(),
    rows: z.number().nullable().optional(),
    piece_size_mm: z.number().optional(),
    // R4 jigsaw version; widens to 1 | 2 only if v2 is ever built. Absent = 1.
    cut_algo_version: z.union([z.literal(1)]).optional(),
    kerf_mm: z.number().optional(),
    include_lakes: z.boolean().optional(),
    satellite_paint: z.boolean().optional(),
    // Satellite tuning bag; `quarter` (the user's Season) selects the CDSE mosaic.
    // MUST stay permissive (`.passthrough().nullish()`): the backend also carries
    // hidden knobs here (stretch_lo_dn/gamma/ks/time_range) that feed the
    // fetch_sentinel2_median cache key, and a strict object would strip them on a
    // regen seed round-trip. `quarter` is typed as the Quarter enum to match the
    // JobConfig interface, with `.catch(undefined)` so a malformed stored value
    // degrades instead of throwing (this schema is REUSED as the response config).
    satellite_paint_config: z
      .object({ quarter: z.enum(['Q1', 'Q2', 'Q3', 'Q4']).optional().catch(undefined) })
      .passthrough()
      .nullish(),
    colors: PreviewColorsSchema.nullable().optional(),
    elevation_splits: z.array(ElevationSplitsWireSchema).nullable().optional(),
    // NO `.max(MAX_MODEL_NAME_LENGTH)` here: this schema is REUSED as the RESPONSE
    // `config` inside JobStatusSchema (below), and legacy stored configs can carry
    // an auto-derived name of thousands of chars (lake joins). A `.max()` would
    // make status parsing throw and break the job page. Outgoing new names are
    // length-capped at the input (`maxLength`) and in maybeSeedBoundsTrailName().
    trail_name: z.string().optional(),
  })
  .passthrough()

/**
 * The POST /pipeline/start REQUEST body: the job config plus the optional
 * Adjust & Regenerate pointer.
 *
 * Deliberately a separate schema instead of another optional key on
 * `JobConfigSchema`: that schema is REUSED as the response `config` inside
 * `JobStatusSchema`, and `source_job_id` is request-only (the backend stores the
 * config, not the pointer). Keeping it here means a stored config that somehow
 * carried the field would never be mistaken for part of a job's identity.
 */
export const StartJobRequestSchema = JobConfigSchema.extend({
  source_job_id: z.string().optional(),
})

/** Accepted POST /pipeline/start response. `job_id` is the SAME id that was sent
 *  as `source_job_id` on an in-place regenerate, and a brand-new one otherwise. */
export const StartJobResponseSchema = z
  .object({
    job_id: z.string(),
    status: z.string(),
  })
  .passthrough()

export const AppliedPaintSchema = z
  .object({
    paint_mode: PaintModeSchema,
    colors: PreviewColorsSchema,
    elevation_splits: z.array(ElevationSplitsWireSchema),
    layers: z.array(PaintLayerSchema).nullable(),
    // The Go status endpoint persists applied_paint with exclude_none semantics,
    // so these keys can be absent (not just null) for non-satellite paint modes.
    // .nullish() (= nullable + optional) tolerates both; consumers already read
    // them with `?.` / `?? null`, so a missing key never reaches them as a value.
    // Coerced for the same reason as the layer params above: seeded campaign
    // applied_paint persists satellite_k as a string ("4"). .nullish() still
    // short-circuits null/undefined before coercion runs.
    satellite_k: z.coerce.number().nullish(),
    satellite_palette_colors: z.array(z.string()).nullish(),
    // R3 (a2): the full per-K palette record (K as a decimal string → ordered
    // hexes). .nullish() for the same reason as the flat pair above: absent on
    // every job saved before R3, null-able on the wire.
    satellite_palettes: z.record(z.string(), z.array(z.string())).nullish(),
    include_trail: z.boolean().optional(),
    include_lake: z.boolean().optional(),
    updated_at: z.string().nullable(),
  })
  .passthrough()

const GeoBoxSchema = z
  .object({ north: z.number(), south: z.number(), east: z.number(), west: z.number() })
  .passthrough()

const XYSchema = z.object({ x: z.number(), y: z.number() }).passthrough()

/**
 * Share cards S1: the real-world scale record the pipeline writes at completion
 * (see `ModelInfo` in types/api.ts). `version` is a plain number so a future
 * version still parses; consumers check it before trusting the numbers.
 */
export const ModelInfoSchema = z
  .object({
    version: z.number(),
    bounds: GeoBoxSchema,
    // The mesh's own lat/lon span; may differ slightly from `bounds` (downsampling).
    mesh_extent: GeoBoxSchema,
    ground_m: XYSchema,
    print_mm: XYSchema,
    scale_mm_per_m: z.number(),
    z_scale: z.number(),
    elev_m: z.object({ min: z.number(), max: z.number() }).passthrough(),
    elev_from_z_mm: z.object({ a: z.number(), b: z.number() }).passthrough(),
  })
  .passthrough()

export const JobStatusSchema = z
  .object({
    job_id: z.string(),
    status: z.enum(['PENDING', 'QUEUED', 'PROCESSING', 'COMPLETE', 'FAILED']),
    current_step: z.string().nullable(),
    current_phase: PhaseKeySchema.nullable(),
    steps_completed: z.array(z.string()),
    steps: z.array(PlannedStepSchema).nullable(),
    phases: z.array(PlannedPhaseSchema).nullable(),
    preview_url: z.string().nullable(),
    production_url: z.string().nullable(),
    // P3 error contract: `error` is now a USER-SAFE string (no traceback). The
    // optional `error_code` / `failure_stage` let the UI derive friendlier copy
    // and fall back to this safe string. `.optional()` (not just `.nullable()`)
    // because legacy in-flight payloads omit them entirely.
    error: z.string().nullable(),
    error_code: z.string().nullable().optional(),
    failure_stage: z.string().nullable().optional(),
    trail_name: z.string().nullable(),
    config: JobConfigSchema.nullable(),
    applied_paint: AppliedPaintSchema.nullable(),
    created_at: z.string(),
    // Free in-place adjusts already used (see REGEN_FREE_LIMIT); the 4th+ adjust
    // of this job is charged as a new generation.
    regen_count: z.number().optional(),
    // The FIXED original bounds the ±30% adjust envelope is measured from (set on
    // the first in-place regenerate, preserved after). Absent until regenerated
    // once; the builder anchors its adjust fence on this so the envelope can't
    // drift outward across successive max-size adjusts.
    regen_anchor_bounds: z
      .object({
        north: z.number(),
        south: z.number(),
        east: z.number(),
        west: z.number(),
      })
      .passthrough()
      .nullable()
      .optional(),
    // Exclusive-collection provenance (E2). Present only on a claimed clone.
    exclusive_id: z.string().nullable().optional(),
    exclusive_collection: z.string().nullable().optional(),
    // 🔴 E2b: the baked satellite entitlement. Its own attribute (not a read of
    // `config.satellite_paint`) because an in-place regenerate overwrites `config`
    // wholesale — see the JobStatus doc in types/api.ts.
    exclusive_satellite: z.boolean().optional(),
    // Per-puzzle unlock ($4.99 `puzzle_unlock` purchase, annual-and-puzzle-unlock).
    // Omitted when false.
    download_unlocked: z.boolean().optional(),
    // Security fix #3: an in-place adjusted exclusive clone loses the FREE
    // exclusive download (exclusive_id is kept as provenance). Omitted when false.
    exclusive_adjusted: z.boolean().optional(),
    // Share cards S1: absent on jobs completed before it shipped and mid in-place
    // regen. `.catch(undefined)` so a malformed record degrades the cards to their
    // no-model_info layout instead of breaking the whole job page.
    model_info: ModelInfoSchema.optional().catch(undefined),
  })
  .passthrough()

// ─── Palette ──────────────────────────────────────────────────────────────────

export const PaletteCentroidSchema = z
  .object({
    palette_index: z.number(),
    color_hex: z.string(),
    pixel_count: z.number(),
  })
  .passthrough()

export const PalettePerKSchema = z
  .object({
    k: z.number(),
    seed: z.number().nullable().optional(),
    centroids: z.array(PaletteCentroidSchema),
  })
  .passthrough()

export const PaletteResponseSchema = z
  .object({
    job_id: z.string(),
    ks: z.array(z.number()),
    palettes: z.record(z.string(), PalettePerKSchema),
  })
  .passthrough()

// ─── Vertex palette meta ──────────────────────────────────────────────────────

export const VertexPaletteMetaPieceSchema = z
  .object({
    name: z.string(),
    vertex_count: z.number(),
    offset: z.number(),
  })
  .passthrough()

export const VertexPaletteMetaSchema = z
  .object({
    ks: z.array(z.number()),
    total_vertices: z.number(),
    pieces: z.array(VertexPaletteMetaPieceSchema),
  })
  .passthrough()

// ─── Terrain preview (F04) ─────────────────────────────────────────────────────

/**
 * Response of `POST /pipeline/terrain-preview`. Mirrors the authoritative
 * contract in docs/features/terrain-preview.md: `bounds` echoes the EXACT
 * extent the grid covers (possibly snapped to DEM cell edges) — the client
 * crops against THIS, never the requested bounds. `data_b64` is base64 of
 * little-endian float32, row-major, length width*height.
 */
export const TerrainPreviewResponseSchema = z
  .object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    bounds: z
      .object({
        north: z.number(),
        south: z.number(),
        east: z.number(),
        west: z.number(),
      })
      .passthrough(),
    elev_min_m: z.number(),
    elev_max_m: z.number(),
    dtype: z.literal('float32'),
    data_b64: z.string(),
  })
  .passthrough()

// ─── Coded error envelope (C-ERR) ──────────────────────────────────────────────

/**
 * Machine-readable error envelope shared across the quota / feature-gate /
 * preview-cap responses (README "Backend error contract"). The backend wraps a
 * coded error as `{"detail":{"error":"<code>", ...extra}}` — the discriminator
 * is always `detail.error` (never a sibling `code`). B4 adds this so the preview
 * UI (C3) can distinguish a `429 preview_limit_exhausted` from any other 4xx;
 * `.passthrough()` keeps extras like `feature` (tier_upgrade_required) and
 * `message` (quota_*). Plain-string `{detail:"..."}` errors stay separate.
 *
 * P3 additive evolution: the backend now also emits `request_id` (mirrored in
 * the `X-Request-Id` header) and an optional per-field `fields` map for
 * `validation_failed` (422) responses. Both modeled here so `parseApiError`
 * round-trips them typed; `.passthrough()` still tolerates any further extras.
 */
export const CodedErrorDetailSchema = z
  .object({
    error: z.string(),
    message: z.string().optional(),
    request_id: z.string().optional(),
    fields: z.record(z.string(), z.string()).optional(),
  })
  .passthrough()

export const CodedErrorResponseSchema = z
  .object({
    detail: CodedErrorDetailSchema,
  })
  .passthrough()

// ─── Usage (B5 / C1) ───────────────────────────────────────────────────────────

/**
 * Runtime schema for `GET /users/{userId}/usage` (the C-USAGE wire contract).
 * Mirrors the `Usage` interface in src/types/api.ts. `allowance`/`remaining`
 * are `.nullable()` because `null` is the SINGLE "unlimited" representation
 * (never a `-1` sentinel). `.passthrough()` at every level so additive backend
 * fields don't break parsing.
 */
// MUST stay in sync with the `Tier` union in src/lib/tiers.ts. This is a RUNTIME
// list, so a new tier added to that union will NOT surface here as a compile
// error — a missing value here silently rejects a valid `/usage` response for
// that tier (see the beta-tier badge regression). The `satisfies readonly
// Tier[]` guard rejects any value that isn't a real Tier, but cannot catch a
// MISSING one; keep this list exhaustive by hand.
//
// A3 closed the missing-value hole with a Go-side parity guard:
// TestTierSchemaZodParity (backend/api-go/internal/platform/tiers/tiers_test.go)
// scans this z.enum([...]) block and fails if any Go tier is absent, or if the
// enum carries a literal that is not a known Go tier.
export const TierSchema = z.enum([
  'trial',
  'beta',
  'starter',
  'premium',
  'pro',
  'commercial',
  'sub_basic',
  'sub_pro',
  'sub_commercial',
  'none',
] satisfies readonly Tier[])

export const UsageSchema = z
  .object({
    tier: TierSchema,
    lifetime: z.boolean(),
    // A5 plan block — always emitted. Required (not .optional()): C1 deploys
    // strictly after A5, so a payload without `plan` is a pre-A5 API and must
    // fail loud rather than silently mis-render (same trailing-deploy rule as
    // every TierSchema change).
    plan: z
      .object({
        source: z.enum(['lifetime', 'stripe', 'none']),
        // stripe-only keys, omitted for non-stripe sources. `status` stays a
        // string (not an enum): B2 writes active | past_due | canceled, but an
        // unknown status must degrade, never reject the whole usage payload.
        status: z.string().optional(),
        renews_at: z.string().optional(), // UTC date "2026-08-06"
        cancel_at_period_end: z.boolean().optional(),
        // annual-and-puzzle-unlock: always present for a stripe source. An
        // unknown value degrades to undefined (read as monthly) rather than
        // rejecting the whole usage payload.
        billing_interval: z.enum(['month', 'year']).optional().catch(undefined),
      })
      .passthrough(),
    generations: z
      .object({
        used_year: z.number().int().nonnegative(),
        allowance: z.number().int().nullable(), // null === unlimited (null ONLY)
        remaining: z.number().int().nullable(), // null === unlimited (null ONLY)
        used_day: z.number().int().nonnegative(),
        daily_cap: z.number().int().positive(),
        yearly_fair_use_cap: z.number().int().positive(),
        effective_yearly_cap: z.number().int().nonnegative(), // 0 for the "none" tier
        // A5 window fields. `remaining`/`used_year` above track the same bucket
        // (`used_year` == `used_period`); `window` says which one.
        window: z.enum(['year', 'cycle']),
        used_period: z.number().int().nonnegative(),
        resets_at: z.string().nullable(), // year → next Jan 1; cycle → cycle_end or null
      })
      .passthrough(),
    previews: z
      .object({
        used_day: z.number().int().nonnegative(),
        daily_cap: z.number().int().positive(),
      })
      .passthrough(),
    features: z
      .object({
        satellite: z.boolean(),
        custom_base: z.boolean(),
        commercial_license: z.boolean(), // A5
        // Older backends predate the download gate; default false (fail-closed)
        // so validation of legacy responses never breaks.
        download: z.boolean().optional().default(false),
      })
      .passthrough(),
  })
  .passthrough()

// ─── Find a Maker (F6) ──────────────────────────────────────────────────────────
// Wire shapes pinned by the SHIPPED F4/F5 handlers, not the older ticket sketch:
// - public list cards carry `cover_photo_url` (string|null) — NOT `thumbnail_url`.
// - list response envelope is `{ makers: [...], next_cursor }` — NOT `{ items }`.
// - public contact is a flat present-fields-only object (public-by-presence).
// - owner-view contact values are `{ value, public }` wrappers.
// - location/photos OMIT absent keys rather than emitting null.
// A material label is a validated free-form STRING, never a fixed enum (SR-16):
// the editor offers a curated quick-pick (lib/makers.ts MATERIALS) but makers may
// add custom materials. The 1-40 char / sanitization rules are enforced at input
// (MakerProfileForm) and by the backend (validate.go); this response-boundary
// schema stays permissive so a valid stored label never fails to parse.
export const MakerMaterialSchema = z.string()

export const MakerColorSchema = z.object({ name: z.string(), hex: z.string() }).passthrough()

export const MakerMulticolorSchema = z
  .object({
    capable: z.boolean(),
    max_colors: z.number().int().nonnegative(),
    colors: z.array(MakerColorSchema).optional().default([]),
  })
  .passthrough()

export const MakerLocationSchema = z
  .object({
    country: z.string(),
    region: z.string().optional(),
    city: z.string().optional(),
    ships_from: z.string().optional(),
    // The admin queue item (admin.go) ALWAYS emits `ships_to`, serializing a nil
    // Go slice as JSON `null` — unlike the public/owner responses, which omit an
    // empty ships_to entirely. So this must accept null (a bare `.optional()`
    // rejects it and crashes the admin queue's parseJson for every maker that
    // never set a ships-to list — the common case).
    ships_to: z.array(z.string()).nullable().optional(),
  })
  .passthrough()

// Public detail gallery entry: { photo_id, url } only.
export const MakerPhotoSchema = z
  .object({
    photo_id: z.string(),
    url: z.string().optional().default(''), // presigned GET; may be "" / omitted
    status: z.enum(['PENDING', 'READY', 'ERROR']).optional(), // owner view only
    moderation_status: z.enum(['pending_review', 'approved', 'rejected']).optional(), // owner view only
  })
  .passthrough()

export const MakerPublicContactSchema = z
  .object({
    store_url: z.string().optional(),
    email: z.string().optional(),
    discord: z.string().optional(),
    instagram: z.string().optional(),
  })
  .passthrough()

export const MakerContactValueSchema = z
  .object({ value: z.string(), public: z.boolean().optional() })
  .passthrough()

export const MakerSelfContactSchema = z
  .object({
    store_url: MakerContactValueSchema.optional(),
    email: MakerContactValueSchema.optional(),
    discord: MakerContactValueSchema.optional(),
    instagram: MakerContactValueSchema.optional(),
  })
  .passthrough()

export const ReviewStatusSchema = z.enum([
  'draft',
  'pending_review',
  'approved',
  'rejected',
  'delisted',
])

export const MakerListItemSchema = z
  .object({
    maker_id: z.string(),
    display_name: z.string(),
    location: MakerLocationSchema,
    materials: z.array(MakerMaterialSchema),
    printers: z.array(z.string()),
    multicolor: MakerMulticolorSchema,
    cover_photo_url: z.string().nullable(),
    bio: z.string().optional(),
    capability_tags: z.array(z.string()).optional(),
    turnaround: z.string().optional(),
    pricing_note: z.string().optional(),
    contact: MakerPublicContactSchema.optional(),
  })
  .passthrough()

export const MakerDetailSchema = MakerListItemSchema.extend({
  photos: z.array(MakerPhotoSchema),
}).passthrough()

export const MakerProfileSchema = z
  .object({
    maker_id: z.string(),
    review_status: ReviewStatusSchema,
    visible: z.boolean(),
    listed: z.boolean(),
    has_pending_version: z.boolean().optional(),
    live_is_published: z.boolean().optional(),
    display_name: z.string(),
    bio: z.string().optional().default(''),
    location: MakerLocationSchema,
    printers: z.array(z.string()),
    materials: z.array(MakerMaterialSchema),
    multicolor: MakerMulticolorSchema,
    contact: MakerSelfContactSchema.optional().default({}),
    photos: z.array(MakerPhotoSchema),
    coc_version: z.string().optional(),
    coc_accepted_at: z.string().optional(),
    turnaround: z.string().optional(),
    pricing_note: z.string().optional(),
    capability_tags: z.array(z.string()).optional(),
    created_at: z.string(),
    updated_at: z.string().optional(),
  })
  .passthrough()

// ─── Billing (C1) ────────────────────────────────────────────────────────────
// Wire schemas for the B4/B5 billing endpoints, consumed by src/api/billing.ts.

/** POST /billing/checkout-session and /billing/portal-session both return a
 * single hosted Stripe URL. `.url()` because both are absolute
 * (https://checkout.stripe.com/…, https://billing.stripe.com/…) — a
 * relative/garbage URL is a backend bug better caught here than at redirect. */
export const BillingUrlResponseSchema = z.object({ url: z.string().url() }).passthrough()

/** GET /billing/checkout-session/{sessionId} — post-redirect verification (B5).
 * `tier` is present iff `synced` is true (the webhook/verify has applied it). */
export const CheckoutSessionVerifySchema = z
  .object({
    session_status: z.enum(['open', 'complete', 'expired']),
    payment_status: z.enum(['paid', 'unpaid', 'no_payment_required']),
    synced: z.boolean(),
    tier: TierSchema.optional(),
    // One-time sessions (annual-and-puzzle-unlock): present for mode=payment
    // sessions even when unpaid; job_id only for puzzle_unlock. A plain string
    // (not an enum): a new one-time SKU must degrade to the neutral return copy,
    // never reject the whole verify payload into an error toast.
    sku: z.string().optional(),
    job_id: z.string().optional(),
  })
  .passthrough()

// ─── Video creator rewards (annual-and-puzzle-unlock) ─────────────────────────

export const RewardPlatformSchema = z.enum([
  'tiktok',
  'instagram',
  'youtube',
  'facebook',
  'x',
  'other',
])
export const RewardLevelSchema = z.enum(['post', 'viral'])
export const RewardStatusSchema = z.enum(['pending', 'approved', 'rejected'])
export const RewardEligibilitySchema = z.enum(['available', 'pending', 'claimed'])

/** The caller's own submission (POST 201 body / GET list item). Optional keys
 * are OMITTED (never null) by the backend when absent. */
export const VideoRewardSubmissionSchema = z
  .object({
    submission_id: z.string(),
    video_url: z.string(),
    platform: RewardPlatformSchema,
    level: RewardLevelSchema,
    status: RewardStatusSchema,
    credit_cents: z.number().int(),
    created_at: z.string(),
    note: z.string().optional(),
    reason: z.string().optional(),
    decided_at: z.string().optional(),
    credit_applied: z.boolean().optional(),
  })
  .passthrough()

export const VideoRewardSubmissionListSchema = z
  .object({
    items: z.array(VideoRewardSubmissionSchema),
    eligibility: z
      .object({
        post: RewardEligibilitySchema,
        viral: RewardEligibilitySchema,
      })
      .passthrough(),
    credit_cents: z
      .object({
        post: z.number().int(),
        viral: z.number().int(),
      })
      .passthrough(),
  })
  .passthrough()

/** Admin view: the user view plus submitter + Stripe bookkeeping. */
export const VideoRewardSubmissionAdminSchema = VideoRewardSubmissionSchema.extend({
  user_id: z.string(),
  user_email: z.string().optional(),
  credit_applied: z.boolean(),
  stripe_balance_txn_id: z.string().optional(),
  decided_by: z.string().optional(),
}).passthrough()

export const VideoRewardAdminPageSchema = z
  .object({
    items: z.array(VideoRewardSubmissionAdminSchema),
    next_cursor: z.string().nullable().optional(),
  })
  .passthrough()

// ─── GPX uploads (C1) ───────────────────────────────────────────────────────────

/**
 * Runtime schemas for the user-uploaded GPX endpoints. Mirror the `Upload` /
 * `CompleteUploadResponse` interfaces in src/types/api.ts. `bbox`/`name`/
 * `num_points` are `.nullable()` because they are null until `/complete` parses
 * the file; `geometry_url` is `.optional()` (only on GET /uploads/{id}).
 * `.passthrough()` at every level so additive backend fields don't break parsing.
 */
const UploadBoundsSchema = z
  .object({
    north: z.number(),
    south: z.number(),
    east: z.number(),
    west: z.number(),
  })
  .passthrough()

const UploadStatusSchema = z.enum(['PENDING', 'READY', 'ERROR'])

export const CreateUploadResponseSchema = z
  .object({
    upload_id: z.string(),
    upload_url: z.string(),
    s3_key: z.string(),
    expires_in: z.number(),
  })
  .passthrough()

export const UploadSchema = z
  .object({
    upload_id: z.string(),
    name: z.string().nullable(),
    bbox: UploadBoundsSchema.nullable(),
    status: UploadStatusSchema,
    num_points: z.number().int().nullable(),
    created_at: z.string(),
    geometry_url: z.string().optional(),
  })
  .passthrough()

export const CompleteUploadResponseSchema = z
  .object({
    upload_id: z.string(),
    status: UploadStatusSchema,
    name: z.string().nullable(),
    bbox: UploadBoundsSchema.nullable(),
    num_points: z.number().int().nullable(),
  })
  .passthrough()

// ─── Campaign ─────────────────────────────────────────────────────────────────

export const CampaignPaletteCentroidSchema = z
  .object({
    palette_index: z.number(),
    color_hex: z.string(),
    pixel_count: z.number(),
  })
  .passthrough()

export const CampaignPalettePerKSchema = z
  .object({
    k: z.number(),
    seed: z.number(),
    centroids: z.array(CampaignPaletteCentroidSchema),
  })
  .passthrough()

export const CampaignPaletteSchema = z
  .object({
    ks: z.array(z.number()),
    palettes: z.record(z.string(), CampaignPalettePerKSchema),
  })
  .passthrough()

export const CampaignPaletteProjectionSchema = z
  .object({
    meta_url: z.string(),
    projections: z.record(z.string(), z.string()),
  })
  .passthrough()

export const CampaignDataSchema = z
  .object({
    slug: z.string(),
    trail_name: z.string(),
    makerworld_url: z.string().nullable(),
    preview_url: z.string(),
    // CS-006: tighten the loose `Record<string, unknown>` to the AppliedPaint
    // schema (resolves the campaign type-looseness finding when adopted in
    // CS-015).
    applied_paint: AppliedPaintSchema,
    palette: CampaignPaletteSchema,
    palette_projection: CampaignPaletteProjectionSchema,
    // Whether the model is a multi-piece puzzle. Non-puzzle (single-part) models
    // have no exploded / cross-section views, so the campaign page hides the
    // view toggle. Optional + default(true) for backward-compat with responses
    // predating the flag (the API defaults absent config.puzzle to true too).
    puzzle: z.boolean().optional().default(true),
  })
  .passthrough()

// ─── z.infer type aliases ─────────────────────────────────────────────────────

export type PreviewColors = z.infer<typeof PreviewColorsSchema>
export type ElevationSplitsWire = z.infer<typeof ElevationSplitsWireSchema>
export type PaintMode = z.infer<typeof PaintModeSchema>
export type PaintLayerParams = z.infer<typeof PaintLayerParamsSchema>
export type PaintLayer = z.infer<typeof PaintLayerSchema>
export type TrailSummary = z.infer<typeof TrailSummarySchema>
export type LakeSummary = z.infer<typeof LakeSummarySchema>
export type TrailCluster = z.infer<typeof TrailClusterSchema>
export type MapResponse = z.infer<typeof MapResponseSchema>
export type LakeMapResponse = z.infer<typeof LakeMapResponseSchema>
export type PlannedStep = z.infer<typeof PlannedStepSchema>
export type PlannedSubStep = z.infer<typeof PlannedSubStepSchema>
export type PlannedPhase = z.infer<typeof PlannedPhaseSchema>
export type JobConfig = z.infer<typeof JobConfigSchema>
export type StartJobRequest = z.infer<typeof StartJobRequestSchema>
export type StartJobResponse = z.infer<typeof StartJobResponseSchema>
export type AppliedPaint = z.infer<typeof AppliedPaintSchema>
export type ModelInfo = z.infer<typeof ModelInfoSchema>
export type JobStatus = z.infer<typeof JobStatusSchema>
export type PaletteCentroid = z.infer<typeof PaletteCentroidSchema>
export type PalettePerK = z.infer<typeof PalettePerKSchema>
export type PaletteResponse = z.infer<typeof PaletteResponseSchema>
export type VertexPaletteMetaPiece = z.infer<typeof VertexPaletteMetaPieceSchema>
export type VertexPaletteMeta = z.infer<typeof VertexPaletteMetaSchema>
export type CampaignData = z.infer<typeof CampaignDataSchema>
export type TerrainPreviewResponse = z.infer<typeof TerrainPreviewResponseSchema>
export type CodedErrorDetail = z.infer<typeof CodedErrorDetailSchema>
export type CodedErrorResponse = z.infer<typeof CodedErrorResponseSchema>
export type Usage = z.infer<typeof UsageSchema>
export type Upload = z.infer<typeof UploadSchema>
export type CreateUploadResponse = z.infer<typeof CreateUploadResponseSchema>
export type CompleteUploadResponse = z.infer<typeof CompleteUploadResponseSchema>
export type MakerMaterial = z.infer<typeof MakerMaterialSchema>
export type MakerColor = z.infer<typeof MakerColorSchema>
export type MakerMulticolor = z.infer<typeof MakerMulticolorSchema>
export type MakerLocation = z.infer<typeof MakerLocationSchema>
export type MakerPhoto = z.infer<typeof MakerPhotoSchema>
export type MakerPublicContact = z.infer<typeof MakerPublicContactSchema>
export type MakerSelfContact = z.infer<typeof MakerSelfContactSchema>
export type MakerContactValue = z.infer<typeof MakerContactValueSchema>
export type MakerListItem = z.infer<typeof MakerListItemSchema>
export type MakerDetail = z.infer<typeof MakerDetailSchema>
export type MakerProfile = z.infer<typeof MakerProfileSchema>
export type ReviewStatus = z.infer<typeof ReviewStatusSchema>

// ─── parseJson helper ─────────────────────────────────────────────────────────

/**
 * Thrown by {@link parseJson} when a payload fails schema validation. A distinct
 * type lets callers tell a DETERMINISTIC parse failure (re-fetching yields the
 * same malformed body) apart from a transient network/HTTP error, so query
 * retry logic can avoid hammering an endpoint that will never validate. See
 * `isRetryableError` in lib/apiError.ts.
 */
export class SchemaValidationError extends Error {
  readonly schemaName: string
  constructor(schemaName: string, message: string) {
    super(message)
    this.name = 'SchemaValidationError'
    this.schemaName = schemaName
  }
}

/**
 * Parse `data` against `schema`, returning the typed result. On a ZodError,
 * rethrows a {@link SchemaValidationError} with a stable, schema-named message
 * so a central reporter can group failures. `schemaName` is required because
 * zod schemas do not carry a stable name at runtime.
 */
export function parseJson<T>(schema: ZodType<T>, data: unknown, schemaName: string): T {
  const result = schema.safeParse(data)
  if (!result.success) {
    throw new SchemaValidationError(
      schemaName,
      `Schema validation failed for ${schemaName}: ${result.error.issues
        .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
        .join('; ')}`,
    )
  }
  return result.data
}
export type RewardPlatform = z.infer<typeof RewardPlatformSchema>
export type RewardLevel = z.infer<typeof RewardLevelSchema>
export type RewardStatus = z.infer<typeof RewardStatusSchema>
export type RewardEligibility = z.infer<typeof RewardEligibilitySchema>
export type VideoRewardSubmission = z.infer<typeof VideoRewardSubmissionSchema>
export type VideoRewardSubmissionList = z.infer<typeof VideoRewardSubmissionListSchema>
export type VideoRewardSubmissionAdmin = z.infer<typeof VideoRewardSubmissionAdminSchema>
